<template>
  <tx-dialog
    v-model="visible" :title="t('favEditor.editFavorites')" width="600px" show-ok-cancel :ok-state="loading ? 'loading' : 'enabled'" :cancel-state="loading ? 'disabled' : 'enabled'" @closed="onClose"
    @ok="doConfirm" @cancel="doCancel"
  >
    <div class="inline-flex flex-wrap">
      <tx-alert class="w-full" :show="tags.length >= appConstants.limits.favoriteTags" type="warning" :text="t('favEditor.tagsLimit', appConstants.limits.favoriteTags)" />
      <div v-for="tag in tags" :key="tag.tag">
        <tag-editor
          v-if="editingTag && tag.id === editingTag.id" :tag="tag" :tags="tags" :show-delete-action="allowDeleteFavorite"
          @ok="onEditTag" @delete="onDeleteTag" @cancel="onCancelEditing"
        />
        <div
          v-else class="flex items-center p-2.5 pr-3 m-1 rounded-md check-box-tag h-10 cursor-pointer"
          :style="{ backgroundColor: tag.color + (tag.selected ? 'FF' : '33'), border: `1px solid ${tag.color}4C`, color: tag.selected ? 'white' : 'inherit' }"
          :class="{ cross: tag.deleted }"
        >
          <tx-checkbox v-model="tag.selected" class="cursor-pointer" :label="tag.tag" :indeterminate="tag.indeterminate" :disabled="tag.deleted" @change="tag.indeterminate = false" />
          <font-awesome-icon v-if="!tag.deleted" class="w-4 h-4 ml-1 hover:text-primary-500" icon="fa-light fa-pencil" @click="doEditTag(tag)" />
        </div>
      </div>

      <div
        v-show="!addingTag && tags.length < appConstants.limits.favoriteTags"
        data-testid="new-tag"
        class="flex items-center p-2 m-1 text-base border border-gray-200 border-solid rounded-md cursor-pointer hover:bg-blue-100 hover:text-primary-500 hover:border-blue-200 active:border-blue-50" @click="doShowAddTag"
      >
        <font-awesome-icon class="w-4 h-4 -ml-1 mr-0.5" icon="fa-light fa-plus" />
        <div class="pr-1 leading-5" v-text="t('favEditor.newTag')" />
      </div>
      <tag-editor v-model:show="addingTag" :tag="defaultTag" :tags="tags" @ok="onAddTag" />
    </div>
  </tx-dialog>
</template>

<script lang="ts" setup>
import { filter, union, without } from 'lodash-es'
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import TagEditor from './TagEditor.vue'
import TxDialog from '@/shared/components/TxDialog.vue'
import TxCheckbox from '@/shared/components/TxCheckbox.vue'
import TxAlert from '@/shared/components/TxAlert.vue'
import type MyArticle from '@/models/myArticle'
import FavoriteTag from '@/models/favoriteTag'
import { appConstants, privileges } from '@/models/constants'
import appConfig from '@/services/appConfig'
import { useUserStore } from '@/store/userData'
import { useBrowseByStore } from '@/store/browseBy'
import SimpleFavoriteTag from '@/models/simpleFavoriteTag'
import type { UpdateFavorite } from '@/api/t1/model/favoriteModel'
import type Article from '@/models/article'
import utils from '@/services/utils'
import { useArticleStore } from '@/store/article'

const { t } = useI18n()
const visible = ref(false)
const userStore = useUserStore()
const tags = ref<ITag[]>([])
const originalTags = {} as { [tag: number]: { selected: boolean, indeterminate: boolean, tag: FavoriteTag } }
const addingTag = ref(false)
const editingTag = ref<ITag>()
const browseByStore = useBrowseByStore()
const articleStore = useArticleStore()

const loading = ref(false)
let articleIds = [] as number[]
const defaultTag: ITag = { tag: '', color: '', selected: true, indeterminate: false, deleted: false, articles: articleIds, customerId: utils.isDefined(userStore.currentCustomer) ? userStore.currentCustomer.CustomerId : -1 }

async function showDialog(articles: MyArticle[]) {
  visible.value = true
  tags.value = []

  let articlesList: Article[] = []
  if (appConfig.DB && userStore.activeCatalog && browseByStore.isBrowseByModel) { // add all the articles of selected Articles
    const modelIds = [...new Set(articles.map(obj => obj.ModelId))]
    const modelArticles = await appConfig.DB.getArticlesByCatalogCodeModelIds(userStore.activeCatalog, userStore.linkedCatalogDetails, modelIds, userStore.currentCustomer, [], false)
    if (modelArticles?.length) {
      modelArticles.forEach((article) => {
        articlesList.push(article)
        articleIds.push(article.Id)
      })
    }
  }
  else {
    articlesList = articles
    articleIds = articles.map(itm => itm.Id)
  }
  const favs = await appConfig.DB!.myFavorites.where({ CatalogCode: userStore.activeCatalog!.CatalogCode, UserId: userStore.userProfile.Id, CreatedByUserName: userStore.currentUsername, Status: 1, CustomerId: userStore.currentCustomer ? userStore.currentCustomer.CustomerId : -1 }).toArray()
  favs.forEach((itm) => {
    const matches = filter(articlesList, art => itm.Articles.includes(art.Id))
    const allSelected = matches.length === articles.length
    const someSelected = matches.length > 0
    tags.value.push({
      id: itm.Id,
      tag: itm.Tag,
      color: itm.Color,
      selected: allSelected,
      indeterminate: !allSelected && someSelected,
      articles: itm.Articles,
      customerId: itm.CustomerId,
      deleted: false,
    })
    originalTags[itm.Id] = { tag: itm, selected: allSelected, indeterminate: !allSelected && someSelected }
  })
}

const allowDeleteFavorite = computed(() => {
  return userStore.userProfile.isValidPrivilege(privileges.favorite.updateStatus)
})

function doCancel() {
  visible.value = false
  onCancelEditing()
}

function onEditTag(tag: ITag) {
  if (editingTag.value) {
    editingTag.value.id = tag.id
    editingTag.value.tag = tag.tag
    editingTag.value.color = tag.color
  }
  onCancelEditing()
}

async function doConfirm() {
  if (userStore.activeCatalog) {
    loading.value = true

    const promises: Promise<void>[] = []
    const deletedTags: FavoriteTag[] = []
    const tagsToCreate: FavoriteTag[] = []
    const tagsToUpdate: { id: number, data: UpdateFavorite, oldTagName: string }[] = []
    // Find the tags that were changed
    tags.value.forEach((itm) => {
      const original = originalTags[itm.id!]
      const tag = original?.tag || new FavoriteTag(userStore.activeCatalog!.CatalogCode, userStore.userProfile.Id, {
        Id: itm.id,
        Tag: itm.tag,
        Color: itm.color,
        CreatedByUserName: userStore.currentUsername,
        Articles: itm.articles,
      })
      if (itm.deleted && itm.id) {
        tag.Status = 0
        tag.LocalUpdatedDate = new Date()
        deletedTags.push(tag)
      }
      else if (!originalTags.hasOwnProperty(itm.id!)
        || originalTags[itm.id!].indeterminate !== itm.indeterminate || originalTags[itm.id!].selected !== itm.selected
      ) {
        tag.Articles = itm.selected ? union(tag.Articles, articleIds) : without(tag.Articles, ...articleIds)
        tag.Color = itm.color
        tag.LocalUpdatedDate = new Date()
        tagsToCreate.push(tag)
        // promises.push(appConfig.DB!.createUpdateFavorite(userStore.activeCatalog!.CatalogCode, tag))
      }
      else {
        const nameChanged = original.tag.Tag !== itm.tag
        const colorChanged = original.tag.Color !== itm.color
        const selectionChanged = original.selected !== itm.selected || original.indeterminate !== itm.indeterminate

        if (nameChanged || colorChanged) {
          // Only update name or color of existig tag
          tagsToUpdate.push({
            id: itm.id!,
            data: {
              Articles: itm.articles,
              Color: itm.color,
              Tag: itm.tag,
              CustomerId: itm.customerId,
            },
            oldTagName: original.tag.Tag,
          })
        }

        if (selectionChanged) {
          tag.Articles = itm.selected ? union(tag.Articles, articleIds) : without(tag.Articles, ...articleIds)
          tag.LocalUpdatedDate = new Date()
          tagsToCreate.push(tag)
        }
      }
    })

    if (deletedTags.length) {
      await appConfig.DB!.deleteFavorite(userStore.activeCatalog.CatalogCode, deletedTags)
    }
    if (tagsToCreate.length) {
      tagsToCreate.forEach((tag) => {
        promises.push(appConfig.DB!.createUpdateFavorite(userStore.activeCatalog!.CatalogCode, userStore.userProfile.Id, tag, userStore.currentCustomer ? userStore.currentCustomer.CustomerId : -1))
      })
    }

    if (tagsToUpdate.length) {
      tagsToUpdate.forEach(({ id, data, oldTagName }) => {
        promises.push(appConfig.DB!.updateFavoriteTag(userStore.activeCatalog!.CatalogCode, oldTagName, userStore.userProfile.Id, id, data))
      })
    }

    await Promise.all(promises)
    // update filter lookup filter after updating the tags
    const favoriteTagsAtt = userStore.myAttributes!._FavoriteTags
    if (favoriteTagsAtt) {
      const favTags = await appConfig.DB!.myFavorites.where({ CatalogCode: userStore.activeCatalog!.CatalogCode, UserId: userStore.userProfile.Id, Status: 1, CustomerId: userStore.currentCustomer ? userStore.currentCustomer.CustomerId : -1 }).toArray()
      favTags.sort((a, b) => {
        if (a.CreatedByUserName.toLowerCase() === userStore.currentUsername.toLowerCase()) { return -1 }
        return a.Tag.localeCompare(b.Tag)
      })
      const sharedTags = await appConfig.DB!.sharedFavorites
        .where({ CatalogCode: userStore.activeCatalog!.CatalogCode, UserId: userStore.userProfile.Id })
        .toArray()

      sharedTags.sort((a, b) => a.Tag.localeCompare(b.Tag))

      const allTags = [...favTags, ...sharedTags]

      favoriteTagsAtt.FilterLookup = new Map()
      allTags.forEach((favoriteTag) => {
        if (!favoriteTagsAtt.FilterLookup.has(favoriteTag.Id)) {
          favoriteTagsAtt.FilterLookup.set(favoriteTag.Id, new SimpleFavoriteTag(favoriteTag.Id, favoriteTag.Tag, favoriteTag.Color, favoriteTag.CreatedByUserName, favoriteTag.CustomerId))
        }
      })
      await articleStore.updateFavTags(favTags, sharedTags)
    }
    loading.value = false
    visible.value = false
  }
}

function doShowAddTag() {
  addingTag.value = true
  onCancelEditing()
}

function onAddTag(tag: ITag) {
  tags.value.push(tag)
}

function doEditTag(tag: ITag) {
  if (tag.deleted) { return }

  editingTag.value = tag
  addingTag.value = false
}

function onDeleteTag(tag: ITag) {
  tag.deleted = true
  onCancelEditing()
}

function onCancelEditing() {
  editingTag.value = undefined
}

function onClose() {
  addingTag.value = false
  onCancelEditing()
}

defineExpose({
  showDialog,
})
</script>

<style lang="scss">
.check-box-tag {
  &.is-checked {
    .el-checkbox__label {
      color: white !important;
    }
  }
}
.cross {
  background: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' version='1.1' preserveAspectRatio='none' viewBox='0 0 100 100'><path d='M1 0 L0 1 L99 100 L100 99' fill='black' /><path d='M0 99 L99 0 L100 1 L1 100' fill='black' /></svg>");
  background-repeat:no-repeat;
  background-position:center center;
  background-size: 100% 100%, auto;
}
</style>
